import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { PayrollPipeline } from '../../components/PayrollPipeline';
import { useReconcile, useEscalations, useEmployees, useAttendanceLive, type AttendanceLive } from '../../hooks';
import { RefreshCw, Activity, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { apiDownload } from '../../lib/api-client';
import { useAuthStore } from '../../lib/store';
import type { Escalation } from '@payroll/shared';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

function getLast6Periods(): string[] {
  const periods: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    periods.push(`${y}-${m}`);
    d.setMonth(d.getMonth() - 1);
  }
  return periods;
}

interface ReconcileResult {
  processed: number;
  escalations_created: number;
}

export const ESC_TYPE_LABEL: Record<string, string> = {
  short_work: 'ทำงานไม่ครบเวลา',
  late: 'เข้างานสาย',
  absent_no_evidence: 'ขาดงาน (ไม่มีหลักฐาน)',
  over_leave_quota: 'ลาเกินสิทธิ์',
  checkin_geofence_failed: 'เช็คอินนอกพื้นที่',
  conditional_evidence_overdue: 'เลยกำหนดส่งหลักฐาน',
  profile_incomplete: 'ประวัติไม่ครบ',
  missing_break_scan: 'ไม่สแกนพักเที่ยง',
  missing_clock_out: 'ไม่สแกนออกงาน',
  emergency_leave_review: 'ลาฉุกเฉินรอตัดสิน',
  no_return_followup: 'ไม่กลับมาทำงาน',
  ot_mismatch: 'OT ไม่ตรงเวลาจริง',
  ot_unrequested: 'OT ไม่ได้ขออนุมัติ',
  unscheduled_work: 'มาทำงานวันที่ไม่ได้กำหนด',
  time_shortfall: 'เข้างานไม่ครบเวลา (หักค่าจ้าง)',
};
const escTypeLabel = (t: string) => ESC_TYPE_LABEL[t] ?? t;

/** "2026-08-26" → "26 ส.ค. 2569" */
const thDay = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** Content-only version used by the PayrollCycle hub. */
export function OwnerReconcileContent() {
  const [period, setPeriod] = useState<string>(getLast6Periods()[0] ?? '');
  const [result, setResult] = useState<ReconcileResult | null>(null);
  const reconcile = useReconcile();
  const { data: escalations } = useEscalations() as { data: Escalation[] | undefined };
  const { data: employees } = useEmployees() as { data: Array<{ emp_code: string; first_name: string; last_name: string; nickname?: string | null }> | undefined };
  // B2 — งวดที่ปิด/จ่ายเงินแล้ว (monthly_summary.locked) ต้องปิดปุ่ม "เริ่มสรุปเวลา"
  // ก่อนกด ไม่ใช่รอให้ 409 มาจาก server. ดึงจาก endpoint เดียวกับ LiveSnapshotCard
  // ด้านล่าง (react-query dedupe คำขอซ้ำให้ ไม่ยิงเพิ่ม).
  const { data: liveData } = useAttendanceLive(period) as { data: AttendanceLive | undefined };
  const periodLocked = liveData?.payroll_snapshot.locked ?? false;
  const empName = new Map((employees ?? []).map((e) => [e.emp_code, `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : '')]));
  const nameOf = (code: string) => empName.get(code) ?? code;
  const toast = useToast();
  const confirm = useConfirm();
  // ไฟล์สำหรับชีตเงินเดือน — มีเงินเดือน+เลขบัตร (server ล็อก OWNER/HR เหมือนกัน)
  const role = useAuthStore((s) => s.profile?.role);
  const canPaysheet = role === 'OWNER' || role === 'HR';
  const [paysheetBusy, setPaysheetBusy] = useState(false);

  async function handlePaysheet() {
    setPaysheetBusy(true);
    try {
      await apiDownload(`/owner/export/paysheet?period=${encodeURIComponent(period)}`, `paysheet-${period}.csv`);
      toast.success(`ดาวน์โหลดไฟล์สำหรับชีตเงินเดือนงวด ${period} แล้ว`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดไม่สำเร็จ');
    } finally {
      setPaysheetBusy(false);
    }
  }

  const pendingEsc = (escalations ?? []).filter((e) => e.status === 'pending');

  async function handleRun() {
    if (!(await confirm({ title: 'สรุปเวลาเข้างาน', message: `รันสรุปเวลางวด ${period}?` }))) return;
    try {
      const res = await reconcile.mutateAsync({ period });
      setResult(res as ReconcileResult);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <>
      <PayrollPipeline current="reconcile" period={period} />
      <div className="card p-4 mb-4">
        <label className="label">เลือกงวด</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="input mb-3">
          {getLast6Periods().map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        {liveData?.from && liveData?.to && (
          <p className="text-xs text-gray-600 -mt-1 mb-3">
            งวดนี้นับวันที่ <strong>{thDay(liveData.from)} – {thDay(liveData.to)}</strong>
          </p>
        )}
        <button onClick={handleRun} disabled={reconcile.isPending || periodLocked} className="btn-primary w-full">
          <RefreshCw className={`w-4 h-4 ${reconcile.isPending ? 'animate-spin' : ''}`} />
          {reconcile.isPending ? 'กำลังประมวลผล...' : 'เริ่มสรุปเวลา'}
        </button>
        {periodLocked && (
          <p className="text-xs text-gray-500 mt-2">
            งวดนี้ปิดและคิดเงินไปแล้ว — ตัวเลขถูกตรึงไว้เพื่อการตรวจสอบ รันสรุปเวลาใหม่ไม่ได้
            ถ้าจำเป็นต้องแก้ไขตัวเลขงวดนี้ ต้องปลดล็อกงวดก่อน
          </p>
        )}
      </div>

      <LiveSnapshotCard period={period} />

      {canPaysheet && (
        <div className="card p-4 mb-4">
          <div className="font-semibold mb-1">ไฟล์สำหรับชีตเงินเดือน</div>
          <p className="text-sm text-gray-500 mb-3">
            เรียงคอลัมน์ตรงกับชีตเงินเดือนของบริษัท ระบบเติมเงินเดือน วันทำงานรายวัน วันนักขัตฤกษ์ OT
            วันหยุดประจำ และยอดหักขาด/ลา/มาสาย ให้ ช่องค่าคอม เงินพิเศษ ความผิดพลาด ประกันร้าน กรอกเองในชีต
            · ใช้ตัวเลขจากการสรุปเวลาล่าสุด ถ้ามีอะไรเปลี่ยนหลังสรุป ต้องกด "เริ่มสรุปเวลา" ใหม่ก่อน
          </p>
          <button onClick={() => void handlePaysheet()} disabled={paysheetBusy} className="btn-secondary w-full">
            <FileSpreadsheet className="w-4 h-4" />
            {paysheetBusy ? 'กำลังสร้างไฟล์...' : `ดาวน์โหลดไฟล์งวด ${period} (CSV)`}
          </button>
        </div>
      )}

      {result && (
        <div className="card p-4 bg-green-50 border-green-200 mb-4">
          <p className="font-medium text-green-800 mb-1">✅ สรุปเวลาเสร็จแล้ว</p>
          <p className="text-sm text-green-700">ประมวลผลพนักงาน: {result.processed} คน</p>
          <p className="text-sm text-green-700">พบรายการผิดปกติใหม่: {result.escalations_created} รายการ</p>
        </div>
      )}

      {pendingEsc.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-700">รายการที่ต้องตรวจสอบ ({pendingEsc.length})</p>
            <Link to="/hr/escalations" className="text-sm font-medium text-brand-700 hover:underline">จัดการทั้งหมด →</Link>
          </div>
          <div className="space-y-2">
            {pendingEsc.map((e) => (
              <div key={e.esc_id} className="card p-3">
                <div className="flex justify-between">
                  <p className="font-medium text-sm">{nameOf(e.emp_code)}</p>
                  <span className="badge-warn">{escTypeLabel(e.type)}</span>
                </div>
                <p className="text-xs text-gray-600">{e.date}</p>
                {e.detail && <p className="text-xs text-gray-500 mt-1">{e.detail}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/** "17 ก.ค. 2569 17:48" — เวลาไทย */
function thaiDateTime(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * ภาพปัจจุบันของงวด — คำนวณสดจากการสแกน/ใบลา/วันหยุดทุกครั้งที่เปิดหน้า
 * ไม่ต้องรอใครกด "สรุปเวลา" ก่อน. วางคู่กับตัวเลขที่ payroll ใช้อยู่ ถ้าต่างกัน
 * = มีอะไรเปลี่ยนหลังสรุปเวลาครั้งล่าสุด ต้องกดสรุปใหม่ก่อนปิดงวด
 */
function LiveSnapshotCard({ period }: { period: string }) {
  const { data, isLoading } = useAttendanceLive(period) as { data: AttendanceLive | undefined; isLoading: boolean };
  const [showDiff, setShowDiff] = useState(false);

  if (isLoading) {
    return <div className="card p-4 mb-4 text-sm text-gray-400">กำลังคำนวณภาพปัจจุบัน…</div>;
  }
  if (!data) return null;

  const t = data.totals;
  const snap = data.payroll_snapshot;
  const cells = [
    { label: 'มาทำงาน', value: t.working_days, unit: 'วัน', tone: 'text-emerald-700' },
    { label: 'ลา', value: t.leave_days, unit: 'วัน', tone: 'text-blue-700' },
    { label: 'ขาดงาน', value: t.absent_days, unit: 'วัน', tone: 'text-red-600' },
    { label: 'สแกนไม่ครบ', value: t.incomplete_scan_days, unit: 'วัน', tone: 'text-amber-700' },
    { label: 'มาสาย', value: t.late_days, unit: `วัน (${t.late_minutes} นาที)`, tone: 'text-amber-700' },
    // fb#438 — เปิดหักเข้างานไม่ครบเวลา: นาทีที่หายตามสรุปเวลาล่าสุด (ก่อนหักวันที่ยกเว้น)
    ...(snap.shortfall_enabled
      ? [{ label: 'เข้างานไม่ครบเวลา', value: snap.shortfall_minutes ?? 0, unit: 'นาที (ตามสรุปเวลา)', tone: 'text-red-600' }]
      : []),
  ];

  return (
    <div className="card p-4 mb-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-semibold flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-brand-600" /> ภาพปัจจุบันของงวด {period}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            คำนวณสดจากการสแกน/ใบลา/วันหยุด ณ {thaiDateTime(data.computed_at)} · พนักงาน {t.headcount} คน
          </p>
        </div>
      </div>

      <div className={`grid grid-cols-2 ${cells.length > 5 ? 'sm:grid-cols-6' : 'sm:grid-cols-5'} gap-2`}>
        {cells.map((c) => (
          <div key={c.label} className="rounded-lg border border-gray-200 px-3 py-2">
            <p className="text-xs text-gray-500">{c.label}</p>
            <p className={`text-lg font-bold leading-tight ${c.tone}`}>{c.value}</p>
            <p className="text-[11px] text-gray-400">{c.unit}</p>
          </div>
        ))}
      </div>

      {/* ตัวเลขที่ payroll ใช้อยู่ตรงกับภาพนี้ไหม */}
      {snap.locked ? (
        <p className="text-xs text-gray-500 mt-3">
          งวดนี้ปิดและคิดเงินไปแล้ว — ตัวเลขที่ระบบใช้คิดเงินถูกตรึงไว้เพื่อการตรวจสอบ
          (สรุปเวลาล่าสุด {snap.computed_at ? thaiDateTime(snap.computed_at) : '-'}) ไม่สามารถรันสรุปเวลาทับได้
          ถ้าจำเป็นต้องแก้ไขตัวเลขงวดนี้ ต้องปลดล็อกงวดก่อน
        </p>
      ) : !snap.exists ? (
        <p className="text-xs text-gray-500 mt-3">
          งวดนี้ยังไม่เคยสรุปเวลา — ตัวเลขด้านบนคือภาพปัจจุบัน กด "เริ่มสรุปเวลา" เพื่อบันทึกให้ระบบเอาไปคิดเงิน
        </p>
      ) : snap.mismatch_count === 0 && !snap.stale ? (
        <p className="text-xs text-emerald-700 mt-3">
          ตัวเลขที่ระบบใช้คิดเงินตรงกับภาพปัจจุบันแล้ว (สรุปเวลาล่าสุด {snap.computed_at ? thaiDateTime(snap.computed_at) : '-'})
        </p>
      ) : (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm text-amber-900 flex items-start gap-1.5">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              ตัวเลขที่ระบบใช้คิดเงินยังเป็นภาพเมื่อ {snap.computed_at ? thaiDateTime(snap.computed_at) : '-'}
              {snap.stale_reason ? ` (${snap.stale_reason})` : ''} — ต่างจากภาพปัจจุบัน{' '}
              <strong>{snap.mismatch_count} คน</strong> · กด "เริ่มสรุปเวลา" ให้ตรงกันก่อนปิดงวด
            </span>
          </p>
          {snap.mismatch_count > 0 && (
            <button onClick={() => setShowDiff((v) => !v)} className="text-xs font-medium text-amber-900 underline mt-2">
              {showDiff ? 'ซ่อนรายชื่อ' : 'ดูว่าใครบ้าง'}
            </button>
          )}
          {showDiff && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-amber-50 text-gray-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left">พนักงาน</th>
                    <th className="px-2 py-1.5 text-right">ขาด (ตอนนี้ / ที่คิดเงิน)</th>
                    <th className="px-2 py-1.5 text-right">ลา (ตอนนี้ / ที่คิดเงิน)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mismatches.slice(0, 50).map((m) => (
                    <tr key={m.emp_code} className="border-t border-gray-100">
                      <td className="px-2 py-1.5">{m.emp_name}<span className="text-gray-400"> · {m.emp_code}</span></td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {m.live.absent_days} / {m.payroll.absent_days}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {m.live.leave_days} / {m.payroll.leave_days}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.mismatches.length > 50 && (
                <p className="text-[11px] text-gray-500 px-2 py-1.5">แสดง 50 คนแรกจาก {data.mismatches.length} คน</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Standalone page kept for legacy /owner/reconcile (now redirects). */
export function OwnerReconcile() {
  return (
    <AdminLayout title="สรุปเวลาเข้างาน">
      <OwnerReconcileContent />
    </AdminLayout>
  );
}
